/* eslint-disable import/order, react-hooks/exhaustive-deps, react/jsx-no-bind, no-restricted-imports, @typescript-eslint/no-empty-function -- pre-existing legacy debt; this change only threads the serviceDate prop through */
import { useTheme, Box, Divider, IconButton, LinearProgress, Stack, SvgIcon, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { InformationCircle } from '@styled-icons/heroicons-outline'
import { APIProvider } from '@vis.gl/react-google-maps'
import { startOfWeek, endOfWeek, format } from 'date-fns'
import { get, sortBy, lowerCase } from 'lodash'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'
import { toast } from 'react-toastify'

import api from 'api'
import {
  CACHE_TAG_WORK_ORDER_DETAILS,
  CACHE_TAG_WORK_ORDER_LIST_WO_SECTION,
  CACHE_TAG_WORK_ORDER_LIST_SUMMARY_WO_SECTION,
  CACHE_TAG_WORK_ORDER_LIST_SUMMARY_CS_SECTION,
} from 'api/cacheTagTypes'
import { useGetServiceDetailsQuery } from 'api/configured-service/getServiceDetails'
import { useFetchAllMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { useLazyGetNearestStopsQuery } from 'api/route/getNearestStops'
import { useLazyGetRoutesMetadataByServiceDateQuery } from 'api/route/getRouteMetadataByServiceDate'
import { useLazyGetRouteStopsQuery } from 'api/route/getRouteStops'
import { useLazyGetRouteStopsPreviewQuery } from 'api/route/getRouteStopsPreview'
import { useGetWorkOrderDetailsQuery } from 'api/work-order/getWorkOrderDetails'
import { HHAlert } from 'components/common/HHAlert'
import HHFullScreenBaseDialog from 'components/common/HHFullScreenBaseDialog'
import { nudgeDeckOverlay } from 'components/common/map/nudgeDeckOverlay'
import ServiceProximityMap from 'components/configured-service/Schedule/ServiceProximityMap'
import NearestStopsDropdown from 'components/configured-service/tabs/MapRoutingTab/components/NearestStopsDropdown'
import useResolvedFilterIds from 'components/configured-service/tabs/MapRoutingTab/hooks/useResolvedFilterIds'
import DesktopRoutePicker from 'components/customer-details/content/routing/DesktopRoutePicker'
import { routeOpFailureMessage } from 'components/route-manager/common/RouteOpResultToast'
import ScheduleStopDialogActions from 'components/route-manager/ScheduleStop/ScheduleStopDialogActions'
import ScheduleStopMap from 'components/route-manager/ScheduleStop/ScheduleStopMap'
import { MapProvider } from 'providers/MapProvider'
import { deserializeDate, formatDateToBEFormatDateFns, formatDateToFEwithWeekday } from 'utils/date'

import { getDistanceBetweenPoints, METERS_IN_MILE } from 'utils/distance'
import { handleError } from 'utils/error'
import { getFeaturePointFromCoordsObj, getUniqueStops, mapStopsToFeaturePoints } from 'utils/map'

import { setIsAssignStopDialogOpen } from 'slices/route/routingActionsSlice'
import { usePreventDoubleSubmit } from 'hooks/usePreventDoubleSubmit'
import ScheduleStopDialogAppBar from 'components/route-manager/ScheduleStop/ScheduleStopDialogAppBar'
import StopsDataGrid from 'components/route-manager/ScheduleStop/StopsDataGrid'
import { mapStopToListItemFormat, isOnRequestStop } from 'utils/route'
import { ROUTE_SCHEDULE_STOP_INVALIDATION_TAGS } from 'utils/routeCacheInvalidation'
import { computeStopInsertionPosition } from 'utils/routeStopOperations'
import { useRouteOperation } from 'hooks/useRouteOperation'
import { useResolveWorkOrderRouteSlotId } from 'hooks/useResolveWorkOrderRouteSlotId'
import { UNASSIGNED_ROUTE } from 'containers/new-route-manager/settings'
import { toIntentStop } from 'components/route-manager/BulkActions/common/toIntentStop'
import { useHandleRouteManagerApiPolling } from 'containers/new-route-manager/common/useHandleRouteManagerApiPolling'
import { getWorkOrderListInvalidatesTags } from 'components/customer-details/content/work-orders/utils'
import { setRoutingTooltipCacheByAccountId } from 'slices/customer/customerDetailsSlice'
import { setResequenceHistory } from 'slices/route/routeSlice'
import { getServiceRecurrenceText } from 'utils/service'
import { buildSequenceEditPrepopulationData } from 'components/route-manager/BulkActions/common/buildEditMasterSequenceData'
import { toPendingMasterStopFields } from 'types/adapters/work-order/toPendingMasterStopFields'
import { setPrePopulatedData } from 'slices/route/masterRouteSlice'
import appRoutes from 'router/routes'
import T from 'T'
import Constants from 'Constants'
import WorkOrderListItem from 'components/work-order/shared/WorkOrderListItem'

import ScheduleStopFiltersPanel from './ScheduleStopFiltersPanel'

// Prefer the WO's pricedServiceType — a recurring configured service can have
// on-request WOs (one-offs); fall back to the CS serviceType only when missing.
const resolvePricedServiceType = (serviceType, woPricedServiceType) => {
  if (woPricedServiceType === 'Recurring' || woPricedServiceType === 'On Request') return woPricedServiceType
  if (serviceType === 'RECURRING' || serviceType === 'Recurring') return 'Recurring'
  if (serviceType === 'ON_REQUEST' || serviceType === 'On Request') return 'On Request'
  return woPricedServiceType || ''
}

const DEFAULT_RADIUS = Math.round(2 * METERS_IN_MILE)

// Maps CS `recurringServiceEvents[].dayOfTheWeek` (e.g. "MONDAY") to the BE's
// single-letter `daysOfWeek` abbreviation consumed by `parseDaysOfWeek`.
const DAY_NAME_TO_ABBR = {
  SUNDAY: 'U',
  MONDAY: 'M',
  TUESDAY: 'T',
  WEDNESDAY: 'W',
  THURSDAY: 'R',
  FRIDAY: 'F',
  SATURDAY: 'S',
}

const defaultValues = {
  sequence: '',
  selectedRoute: '',
  selectedNearestStop: '',
  startDate: startOfWeek(new Date(), { weekStartsOn: 0 }),
  endDate: endOfWeek(new Date(), { weekStartsOn: 0 }),
  radius: DEFAULT_RADIUS,
  radiusCommitted: DEFAULT_RADIUS,
  radiusUnit: 'mi',
  measure: [],
  material: [],
  action: [],
  businessLine: [],
  selectedDays: {
    Sunday: true,
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true,
  },
  selectedRoutes: {},
}

const getStopsByRouteId = routes => {
  const stopsByRouteId = {}
  routes.forEach(route => {
    const { id, stops } = route
    const sortedStops = sortBy(stops, ['sequence'])
    stopsByRouteId[id] = sortedStops.map((s, index) => ({ ...s, position: index }))
  })
  return stopsByRouteId
}

const formatStops = (stops, workOrderId) =>
  stops.map(stop => {
    const listItem = mapStopToListItemFormat(stop)
    return {
      ...stop,
      ...listItem,
      toBeInserted: workOrderId === listItem.workOrderId,
      isUnassigned: false,
    }
  })

const insertElementAtPosition = (rows, toBeInserted, sequenceNumber = 0, isAssignedAndSameRoute = false) => {
  const updatedRows = [...rows]
  const fixedSequenceNumber = Math.max(sequenceNumber, 1)

  if (isAssignedAndSameRoute) {
    // Swap logic
    const currentPosition = rows.findIndex(row => row.workOrderId === toBeInserted.workOrderId)
    const element = updatedRows[currentPosition]
    updatedRows.splice(currentPosition, 1)
    updatedRows.splice(fixedSequenceNumber - 1, 0, element)
    return updatedRows
  }

  if (fixedSequenceNumber > updatedRows.length) {
    updatedRows.push(toBeInserted)
  } else {
    updatedRows.splice(fixedSequenceNumber - 1, 0, toBeInserted)
  }
  return updatedRows
}

const ScheduleStopDialog = () => {
  const navigate = useNavigate()
  const formMethods = useForm({
    defaultValues,
  })
  const customerDetails = useSelector(state => get(state, 'CustomersReducer.customerDetails', null), shallowEqual)
  const accountId = get(customerDetails, 'id')
  const tooltipStopCacheByAccountId = useSelector(state => get(state, 'CustomerDetails.routing.tooltipStopCacheByAccountId', {}))
  const accountTooltipStopCacheByDate = get(tooltipStopCacheByAccountId, accountId, {})
  const apiRef = useGridApiRef()
  const mapRef = useRef()
  const { watch, setFocus, reset, control, handleSubmit, setValue } = formMethods
  const selectedSequence = watch('sequence')
  const selectedRoute = watch('selectedRoute')
  const selectedNearestStop = watch('selectedNearestStop')
  const isOpen = useSelector(s => s.routingActions.assignStopDialog.isOpen)
  const stopToBeAssignedWorkOrderId = useSelector(s => get(s, 'routingActions.assignStopDialog.stopToBeAssigned.workOrderId'))
  const theme = useTheme()
  const dispatch = useDispatch()
  const [routingMode, setRoutingMode] = useState('nearest')
  const [mapMarkerType, setMapMarkerType] = useState('weekDay')
  const handleRoutingModeChange = useCallback((_, newMode) => {
    if (newMode !== null) setRoutingMode(newMode)
  }, [])
  const handleMarkerTypeChange = useCallback((_, newType) => {
    if (newType !== null) setMapMarkerType(newType)
  }, [])
  const [getRouteStops, { isLoading, isFetching }] = useLazyGetRouteStopsQuery()
  const [rows, setRows] = useState([])
  const [toBeAssignedGeojsonData, setToBeAssignedGeojsonData] = useState([])
  const [getRoutesMetadata, { data: routeMetaData, isFetching: isMetadataFetching }] = useLazyGetRoutesMetadataByServiceDateQuery()
  const [getRouteStopsPreview] = useLazyGetRouteStopsPreviewQuery()
  const { data: workOrderDetailsData, currentData: currentWorkOrderDetailsData } = useGetWorkOrderDetailsQuery(
    { workOrderId: stopToBeAssignedWorkOrderId },
    { skip: !isOpen || !stopToBeAssignedWorkOrderId }
  )
  const woConfiguredServiceId = get(workOrderDetailsData, 'configuredServiceId')
  const { data: serviceDetailsData } = useGetServiceDetailsQuery(woConfiguredServiceId, {
    skip: !woConfiguredServiceId,
  })
  const [routeGeojsonData, setRoutePointGeojsonData] = useState()
  const [fitBounds, setFitBounds] = useState(false)
  const actionSerializedServiceDate = useSelector(state => get(state, 'routingActions.actionSerializedServiceDate'))
  const routingSerializedServiceDate = useSelector(state => get(state, 'CustomerDetails.routing.serializedServiceDate'))
  const effectiveSerializedServiceDate = actionSerializedServiceDate || routingSerializedServiceDate
  const serviceDate = effectiveSerializedServiceDate ? deserializeDate(effectiveSerializedServiceDate) : new Date()
  const beFormatServiceDate = formatDateToBEFormatDateFns(serviceDate)
  const [stopsByRouteId, setStopsByRouteId] = useState({})
  const routes = get(routeMetaData, 'routes', [])
  const [getNearestStops, { data: nearestStopsData, isFetching: isNearestFetching }] = useLazyGetNearestStopsQuery()
  const { data: metaFiltersData } = useFetchAllMetaFiltersQuery()
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  const filterOptions = useMemo(() => {
    const { businessLines = [], materials = [], measure = [], serviceAction = [] } = metaFiltersData || {}
    return {
      businessLineOptions: (businessLines || [])
        .filter(bl => bl.active)
        .map(bl => ({ label: bl.businessLineLabel, value: bl.businessLineLabel, id: bl.id })),
      materialOptions: (materials || []).map(m => ({ label: m.materialType, value: m.materialType, id: m.id })),
      measureOptions: (measure || []).map(m => ({ label: `${m.volume} ${m.unit}`, value: `${m.volume} ${m.unit}`, id: m.id })),
      serviceActionOptions: (serviceAction || []).map(sa => ({ label: sa.actionType, value: sa.actionType, id: sa.id })),
    }
  }, [metaFiltersData])

  const {
    measureIds: resolvedMeasureIds,
    materialIds: resolvedMaterialIds,
    actionIds: resolvedActionIds,
    businessLineIds: resolvedBusinessLineIds,
    radiusCommitted,
  } = useResolvedFilterIds(watch, filterOptions, 500)

  const isRouteSelected = Boolean(selectedRoute)
  const rawStopToBeAssigned = useSelector(s => s.routingActions.assignStopDialog.stopToBeAssigned, shallowEqual)
  const routeId = get(rawStopToBeAssigned, 'routeId')
  const workOrderId = get(rawStopToBeAssigned, 'workOrderId')
  const sequence = get(rawStopToBeAssigned, 'sequence')
  // Callers assigning by nearest stop (board-v2 nearest-stop drop) seed a route +
  // computed sequence but want the dialog to open on the Nearest stop tab, not By
  // route — so the user sees the nearest-stop pick they're reviewing.
  const preferNearestMode = get(rawStopToBeAssigned, 'preferNearestMode', false)
  const filteredStops = get(stopsByRouteId, selectedRoute, [])
  const loading = isLoading || isFetching

  // Billing Review lists work orders without their stop position, so read it off the route once its stops load.
  const seededSequence = useMemo(() => {
    if (sequence || !workOrderId) return sequence
    const ownStop = stopsByRouteId[routeId]?.find(s => s.workOrderId === workOrderId || s.id === workOrderId)
    return ownStop ? ownStop.position + 1 : sequence
  }, [sequence, stopsByRouteId, routeId, workOrderId])

  const nearestStopsList = useMemo(() => {
    if (!nearestStopsData?.routes) return []
    const stopLat = get(rawStopToBeAssigned, 'latitude')
    const stopLng = get(rawStopToBeAssigned, 'longitude')
    return nearestStopsData.routes
      .flatMap(route =>
        (route.stops || []).map(stop => {
          let { distance } = stop
          if (distance === undefined && stopLat && stopLng) {
            distance = getDistanceBetweenPoints(stopLat, stopLng, stop.latitude || stop.lat, stop.longitude || stop.lng)
          }
          return {
            ...stop,
            route: { id: route.id, name: route.name, color: route.color },
            id: stop.id || stop.workOrderId,
            locationName: stop.locationName || stop.accountName || stop.serviceName,
            addressLine1: stop.line1 || stop.unitNumber,
            distance,
          }
        })
      )
      .sort((a, b) => (a.distance || 0) - (b.distance || 0))
  }, [nearestStopsData, rawStopToBeAssigned])

  const handleNearestStopSelect = useCallback(
    stop => {
      const targetRouteId = stop.routeId || stop.route?.id
      if (!targetRouteId) return
      // Match the MenuItem value priority so selectedNearestStop tracks the field.
      const targetStopId = stop.id || stop.workOrderId
      // Pass the picked stop's coordinates so the distance fallback in
      // computeStopInsertionPosition resolves against IT (not the to-be-assigned
      // stop) when the nearest-stops API id doesn't match any route stop.
      const targetLat = stop.latitude ?? stop.lat
      const targetLng = stop.longitude ?? stop.lng
      setValue('selectedRoute', targetRouteId)
      setValue('selectedNearestStop', targetStopId)
      const routeStops = get(stopsByRouteId, targetRouteId, [])
      if (routeStops.length > 0) {
        const { sequence: computedSequence } = computeStopInsertionPosition({
          routeStops,
          targetStopId,
          lat: targetLat,
          lng: targetLng,
        })
        setValue('sequence', computedSequence || 1)
      } else {
        // Empty target route — default to position 1 so the user doesn't have
        // to type it manually.
        setValue('sequence', 1)
      }
    },
    [setValue, stopsByRouteId]
  )

  const stopToBeAssigned = useMemo(() => {
    if (!workOrderId) return rawStopToBeAssigned

    let enriched = { ...rawStopToBeAssigned }

    const allRouteStops = stopsByRouteId ? Object.values(stopsByRouteId).flat() : []
    const routeStop = allRouteStops.find(s => s.workOrderId === workOrderId || s.id === workOrderId)
    if (routeStop) enriched = { ...enriched, ...routeStop }

    // `configuredServiceEventType` ("Recurring" | "One Off") distinguishes a
    // one-off event inside a recurring service — pricedServiceType still reads
    // "Recurring" in that case. Also flatten nested account/location/address
    // into the flat fields the master tile reads. Prior layers win; details fill gaps.
    if (workOrderDetailsData) {
      const flat = toPendingMasterStopFields(workOrderDetailsData)
      const fillIfEmpty = (current, fallback) => (current !== undefined && current !== null && current !== '' ? current : fallback)
      enriched = {
        ...enriched,
        configuredServiceEventId: enriched.configuredServiceEventId || get(workOrderDetailsData, 'configuredServiceEventId'),
        configuredServiceId: enriched.configuredServiceId || get(workOrderDetailsData, 'configuredServiceId'),
        serviceDate: enriched.serviceDate || get(workOrderDetailsData, 'serviceDate'),
        workOrderType: enriched.workOrderType || get(workOrderDetailsData, 'workOrderType'),
        configuredServiceEventType: enriched.configuredServiceEventType || get(workOrderDetailsData, 'configuredServiceEventType'),
        accountId: fillIfEmpty(enriched.accountId, flat.accountId),
        accountName: fillIfEmpty(enriched.accountName, flat.accountName),
        locationId: fillIfEmpty(enriched.locationId, flat.locationId),
        addressName: fillIfEmpty(enriched.addressName, flat.addressName),
        line1: fillIfEmpty(enriched.line1, flat.line1),
        unitNumber: fillIfEmpty(enriched.unitNumber, flat.unitNumber),
        city: fillIfEmpty(enriched.city, flat.city),
        state: fillIfEmpty(enriched.state, flat.state),
        zipCode: fillIfEmpty(enriched.zipCode, flat.zipCode),
        country: fillIfEmpty(enriched.country, flat.country),
        latitude: enriched.latitude ?? flat.latitude,
        longitude: enriched.longitude ?? flat.longitude,
        nearestRoadLatitude: enriched.nearestRoadLatitude ?? flat.nearestRoadLatitude,
        nearestRoadLongitude: enriched.nearestRoadLongitude ?? flat.nearestRoadLongitude,
      }
    }

    // Layer 4: merge configured service details (recurrenceFrequency, recurrencePer, recurrenceInterval, serviceType)
    if (serviceDetailsData) {
      const serviceRecurrence = getServiceRecurrenceText(serviceDetailsData)
      const svcRecurrenceFrequency = get(serviceDetailsData, 'recurrenceFrequency', enriched.recurrenceFrequency || 1)
      const svcRecurrencePer = get(serviceDetailsData, 'recurrencePer', enriched.recurrencePer || '')
      const svcRecurrenceInterval = get(serviceDetailsData, 'recurrenceInterval', enriched.recurrenceInterval || '')
      const svcServiceType = get(serviceDetailsData, 'serviceType', '')
      // Fallback for brand-new unassigned WOs where Layer 2 can't supply `daysOfWeek`.
      const svcDaysOfWeek =
        enriched.daysOfWeek ||
        (get(serviceDetailsData, 'recurringServiceEvents', []) || [])
          .map(e => DAY_NAME_TO_ABBR[String(e?.dayOfTheWeek || '').toUpperCase()])
          .filter(Boolean)
          .join('')
      enriched = {
        ...enriched,
        recurrenceFrequency: svcRecurrenceFrequency,
        recurrencePer: svcRecurrencePer,
        recurrenceInterval: svcRecurrenceInterval,
        pricedServiceType: resolvePricedServiceType(svcServiceType, enriched.pricedServiceType),
        serviceRecurrence,
        daysOfWeek: svcDaysOfWeek,
      }
    }

    const hasAnyEnrichment = routeStop || workOrderDetailsData || serviceDetailsData
    if (!hasAnyEnrichment) return rawStopToBeAssigned

    const mapped = mapStopToListItemFormat(enriched)
    return { ...enriched, ...mapped, toBeInserted: true }
  }, [rawStopToBeAssigned, workOrderId, stopsByRouteId, workOrderDetailsData, serviceDetailsData])

  const isStopOnRequest = isOnRequestStop(stopToBeAssigned)

  // Returns the first valid, non-zero coordinate by walking sources × fields.
  // `0` is intentionally rejected (distinct addresses, never literal origin).
  const resolveCoordinate = (sources, fields) =>
    sources
      .filter(Boolean)
      .flatMap(source => fields.map(field => get(source, field)).filter(raw => raw !== null && raw !== undefined && raw !== ''))
      .map(raw => parseFloat(raw))
      .find(num => Number.isFinite(num) && num !== 0)

  const proximityMapLatitude = useMemo(
    () =>
      resolveCoordinate(
        [stopToBeAssigned, rawStopToBeAssigned, workOrderDetailsData],
        ['latitude', 'serviceLatitude', 'lat', 'address.latitude', 'serviceLocation.latitude']
      ),
    [stopToBeAssigned, rawStopToBeAssigned, workOrderDetailsData]
  )

  const proximityMapLongitude = useMemo(
    () =>
      resolveCoordinate(
        [stopToBeAssigned, rawStopToBeAssigned, workOrderDetailsData],
        ['longitude', 'serviceLongitude', 'lng', 'address.longitude', 'serviceLocation.longitude']
      ),
    [stopToBeAssigned, rawStopToBeAssigned, workOrderDetailsData]
  )

  useEffect(() => {
    if (!isOpen) return
    const stopLat = parseFloat(
      get(rawStopToBeAssigned, 'latitude') || get(rawStopToBeAssigned, 'serviceLatitude') || get(rawStopToBeAssigned, 'lat')
    )
    const stopLng = parseFloat(
      get(rawStopToBeAssigned, 'longitude') || get(rawStopToBeAssigned, 'serviceLongitude') || get(rawStopToBeAssigned, 'lng')
    )
    if (stopLat && stopLng && beFormatServiceDate) {
      getNearestStops({
        latitude: stopLat,
        longitude: stopLng,
        serviceDate: beFormatServiceDate,
        limit: 20,
        radiusMeters: radiusCommitted || DEFAULT_RADIUS,
        ...(resolvedMeasureIds.length > 0 && { measureIds: resolvedMeasureIds }),
        ...(resolvedMaterialIds.length > 0 && { materialIds: resolvedMaterialIds }),
        ...(resolvedActionIds.length > 0 && { actionIds: resolvedActionIds }),
        ...(resolvedBusinessLineIds.length > 0 && { businessLines: resolvedBusinessLineIds }),
      })
    }
  }, [
    isOpen,
    rawStopToBeAssigned,
    beFormatServiceDate,
    radiusCommitted,
    resolvedMeasureIds,
    resolvedMaterialIds,
    resolvedActionIds,
    resolvedBusinessLineIds,
  ])

  const handleMapStopClick = useCallback(
    position => {
      if (position) {
        setValue('sequence', position + 1)
      }
    },
    [setValue]
  )

  const handleResetWithExistingRoute = () => {
    reset({ sequence: seededSequence, selectedRoute: routeId })
  }

  const handleReset = () => {
    reset(defaultValues)
    setRows([])
    setRoutePointGeojsonData(undefined)
  }

  const handleClose = () => {
    dispatch(setIsAssignStopDialogOpen(false))
  }

  const handleCancel = () => {
    handleReset()
    handleClose()
  }

  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)

  const { run: runRouteOperation } = useRouteOperation()
  const resolveRouteSlotId = useResolveWorkOrderRouteSlotId()

  const runScheduleStopOperation = useCallback(
    async includeMaster => {
      const isPendingSlotMissing = rows.some(row => row.toBeInserted && !row.routeSlotId)
      const pendingRouteSlotId = isPendingSlotMissing
        ? await resolveRouteSlotId({ workOrderId, routeId, serviceDate: beFormatServiceDate })
        : null
      const withPendingSlot = row => (row.toBeInserted && !row.routeSlotId ? { ...row, routeSlotId: pendingRouteSlotId } : row)
      const pendingStops = rows.filter(r => r.toBeInserted).map(row => toIntentStop(withPendingSlot(row)))
      const allOrderedStops = rows.map(row => toIntentStop(withPendingSlot(row)))

      // A move, not an assign: under v3 only the move inserts relative to a neighbour and tells the route the stop leaves.
      const result = await runRouteOperation({
        kind: 'move',
        routeId: selectedRoute,
        sourceRouteId: routeId || UNASSIGNED_ROUTE.id,
        serviceDate: beFormatServiceDate,
        stops: pendingStops,
        destinationOrderedStops: allOrderedStops,
        includeMaster,
      })
      if (!result.success) {
        throw new Error(routeOpFailureMessage(result, 'save failed'))
      }
    },
    [runRouteOperation, resolveRouteSlotId, workOrderId, routeId, selectedRoute, beFormatServiceDate, rows]
  )

  const handleSaveSuccess = useCallback(() => {
    const csId = get(stopToBeAssigned, 'configuredServiceId') || woConfiguredServiceId
    // Pass csId so the configured-service-scoped section tags get invalidated too
    // (work orders on the customer details services tab are keyed by csId).
    const woInvalidateTagsMonth = getWorkOrderListInvalidatesTags({ isNew: true, serviceDate, csId })
    const woInvalidateTagsSection = getWorkOrderListInvalidatesTags({ isNew: true, sectionWise: true, csId })
    const woInvalidateTagsList = getWorkOrderListInvalidatesTags({ isNew: false })
    const woInvalidateTagsSummary = getWorkOrderListInvalidatesTags({ isNew: true, sectionWise: true })
    const woInvalidateTags = Array.from(
      new Set([
        ...(woInvalidateTagsMonth || []),
        ...(woInvalidateTagsSection || []),
        ...(woInvalidateTagsList || []),
        ...(woInvalidateTagsSummary || []),
        // Broad tags that cover the calendar's fetchWorkOrderList query (keyed by WO section + plain list)
        CACHE_TAG_WORK_ORDER_LIST_WO_SECTION,
        CACHE_TAG_WORK_ORDER_LIST_SUMMARY_WO_SECTION,
        CACHE_TAG_WORK_ORDER_LIST_SUMMARY_CS_SECTION,
      ])
    )
    // Include the single WO-details tag so the WO detail page's route chip refreshes after
    // assign / change-route (the more-menu now opens this dialog from there); the 1500ms retry
    // below covers the BE assignment-commit lag that otherwise required a hard refresh.
    const tags = [...ROUTE_SCHEDULE_STOP_INVALIDATION_TAGS, ...woInvalidateTags, CACHE_TAG_WORK_ORDER_DETAILS]

    handleReset()
    handleClose()

    // Invalidate AFTER close (setTimeout 0) so RTKQ subscribers unmount before
    // tags fire; the second tick at 1500ms catches BE master-sequence writes
    // that commit slightly later.
    if (accountId) {
      dispatch(
        setRoutingTooltipCacheByAccountId({
          [accountId]: {
            ...accountTooltipStopCacheByDate,
            [beFormatServiceDate]: undefined,
          },
        })
      )
    }
    setTimeout(() => {
      dispatch(api.util.invalidateTags(tags))
      dispatch(setResequenceHistory({ track: true }))
    }, 0)
    setTimeout(() => dispatch(api.util.invalidateTags(tags)), 1500)
  }, [
    serviceDate,
    beFormatServiceDate,
    accountId,
    accountTooltipStopCacheByDate,
    stopToBeAssigned,
    woConfiguredServiceId,
    dispatch,
    handleReset,
    handleClose,
  ])

  // Ref-based gate — local isSaving / isPublishing flags only flip after a render.
  const handleSave = usePreventDoubleSubmit(() => {
    setIsSaving(true)
    const savePromise = runScheduleStopOperation(false).then(handleSaveSuccess)

    savePromise.catch(e => handleError(e)).finally(() => setIsSaving(false))

    toast.promise(savePromise, {
      pending: T.SAVING_SERVICE,
      success: T.ROUTING_SAVED,
    })

    return savePromise
  })

  const handlePublishMasterSequence = usePreventDoubleSubmit(() => {
    const hasCseId = Boolean(get(stopToBeAssigned, 'configuredServiceEventId'))
    if (isStopOnRequest || !hasCseId) return undefined
    setIsPublishing(true)
    const publishPromise = runScheduleStopOperation(true).then(handleSaveSuccess)

    publishPromise.catch(e => handleError(e)).finally(() => setIsPublishing(false))

    toast.promise(publishPromise, {
      pending: T.SAVING_SERVICE,
      success: `${T.MASTER_SEQUENCE_PUBLISHED_SUCCESSFULLY}. ${T.CHECK_MASTER_SEQUENCE_TO_REVIEW_CHANGES}`,
    })

    return publishPromise
  })

  const [isSavingMaster, setIsSavingMaster] = useState(false)

  const handleEditMasterSequence = usePreventDoubleSubmit(async () => {
    if (!selectedRoute || !get(stopToBeAssigned, 'configuredServiceEventId') || isStopOnRequest) return
    setIsSavingMaster(true)
    try {
      const dayOfWeek = lowerCase(format(serviceDate, 'EEEE'))
      const assignedCseId = get(stopToBeAssigned, 'configuredServiceEventId')
      const targetSequence = Number(selectedSequence) || 1
      const isAlreadyOnRoute = routeId === selectedRoute

      let prepopData
      if (isAlreadyOnRoute) {
        prepopData = {
          resequenceStopId: assignedCseId,
          assignSequence: targetSequence,
        }
      } else {
        const prepopRows = rows
          .filter(row => !(row.configuredServiceEventId === assignedCseId && !row.toBeInserted))
          .map(row => (row.toBeInserted ? { ...row, routeId: null } : row))
        prepopData = await buildSequenceEditPrepopulationData(prepopRows, selectedRoute, serviceDate, getRouteStopsPreview)
        prepopData.assignSequence = targetSequence
      }
      prepopData.scrollTargetCseId = assignedCseId

      dispatch(setPrePopulatedData(prepopData))
      handleReset()
      handleClose()
      navigate(`${appRoutes.app.masterSequence}?route_id=${selectedRoute}&day_of_week=${dayOfWeek}`)
    } catch (e) {
      handleError(e)
    } finally {
      setIsSavingMaster(false)
    }
  })

  const handleClearSelection = useCallback(() => {
    setValue('selectedRoute', '')
    setValue('selectedNearestStop', '')
    setValue('sequence', '')
    setRows([])
    setRoutePointGeojsonData(undefined)
  }, [setValue])

  const isAssignedAndSameRoute = stops => routeId === selectedRoute && stops.find(stop => stop.workOrderId === workOrderId)

  const handleRouteChange = () => {
    setTimeout(() => {
      setFocus('sequence')
    }, 300)
  }

  useEffect(() => {
    // On-request stops can't use nearest mode — except the assign-by-nearest flow,
    // which explicitly opts in and seeds the target route + nearest sequence.
    if (isStopOnRequest && !preferNearestMode) setRoutingMode('byRoute')
  }, [isStopOnRequest, preferNearestMode])

  // Apply the seeded route/sequence/tab. Keyed on the seed itself (routeId +
  // workOrderId) — not just isOpen — so a fresh assign (e.g. a different target
  // route from the board's nearest-stop drop) always re-selects the seeded target
  // route instead of stranding the previous selection.
  useEffect(() => {
    if (!isOpen || !routeId) return
    // Nearest-stop callers seed a route but want the Nearest stop tab; everyone
    // else (existing assign flows) opens on By route as before.
    setRoutingMode(preferNearestMode ? 'nearest' : 'byRoute')
    setValue('selectedRoute', routeId)
    // Default to position 1 when the caller didn't provide an explicit sequence —
    // saves the user from typing it manually.
    setValue('sequence', sequence || 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, routeId, workOrderId, preferNearestMode])

  // `routes: []` means "every route" to the BE — 4 MB on large tenants, parsed
  // on the main thread before the dialog became usable.
  const routeToLoad = selectedRoute || routeId
  const loadedRouteKeysRef = useRef(new Set())

  useEffect(() => {
    if (!isOpen) {
      loadedRouteKeysRef.current = new Set()
      setStopsByRouteId({})
      return
    }
    if (!routeToLoad) return
    const routeKey = `${beFormatServiceDate}|${routeToLoad}`
    if (loadedRouteKeysRef.current.has(routeKey)) return
    loadedRouteKeysRef.current.add(routeKey)

    getRouteStops({
      skipCache: true,
      routes: [routeToLoad],
      serviceDate: beFormatServiceDate,
      fetchId: routeKey,
    })
      .unwrap()
      .then(r => {
        // Closing the dialog swaps in a fresh key set, so a request that resolves
        // after that must not repopulate the state the close just cleared.
        if (!loadedRouteKeysRef.current.has(routeKey)) return
        setStopsByRouteId(prev => ({ ...prev, ...getStopsByRouteId(get(r, 'routes', [])) }))
      })
      .catch(() => {
        // Allow a retry on the next selection of this route.
        loadedRouteKeysRef.current.delete(routeKey)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRouteStops identity churns per render
  }, [isOpen, routeToLoad, beFormatServiceDate])

  useEffect(() => {
    if (!selectedRoute) return
    setFitBounds(true)
    const routeStops = get(stopsByRouteId, selectedRoute, [])

    // Don't recompute sequence until stops load — would clobber a user/preset value.
    if (routeStops.length === 0) return

    if (routeId === selectedRoute && seededSequence && !selectedNearestStop) {
      setValue('sequence', seededSequence)
      return
    }

    // When a nearest stop is picked, use ITS coordinates for the distance
    // fallback so the fallback resolves to the stop the user chose.
    const pickedStop = selectedNearestStop ? nearestStopsList.find(s => (s.id || s.workOrderId) === selectedNearestStop) : null
    const fallbackLat = pickedStop ? pickedStop.latitude ?? pickedStop.lat : proximityMapLatitude
    const fallbackLng = pickedStop ? pickedStop.longitude ?? pickedStop.lng : proximityMapLongitude

    const { sequence: computedSequence } = computeStopInsertionPosition({
      routeStops,
      targetStopId: selectedNearestStop || undefined,
      lat: fallbackLat,
      lng: fallbackLng,
    })
    setValue('sequence', computedSequence)
    const formattedStops = formatStops(filteredStops, workOrderId)
    const updatedPointGeojsonData = mapStopsToFeaturePoints([...formattedStops])
    const { latitude, longitude, ...rest } = stopToBeAssigned
    const toBeAssignedFeature = getFeaturePointFromCoordsObj({
      latitude,
      longitude,
      properties: {
        latitude,
        longitude,
        ...rest,
        icon: 'to-be-assigned-marker',
      },
    })
    setToBeAssignedGeojsonData([toBeAssignedFeature])
    setRoutePointGeojsonData(updatedPointGeojsonData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsByRouteId, selectedRoute, selectedNearestStop, proximityMapLatitude, proximityMapLongitude, nearestStopsList])

  useEffect(() => {
    if (isEmpty(stopToBeAssigned)) return
    const formattedStops = formatStops(filteredStops, workOrderId)
    if (isAssignedAndSameRoute(formattedStops)) {
      setRows([...formattedStops])
      return
    }
    // Insert at the computed/selected sequence — NOT the front. The saved order
    // comes from `rows` (allOrderedStops; toIntentStop carries no sequence), and
    // the only thing that used to move the stop off #1 was the debounced,
    // apiRef-gated reposition that re-runs only on selectedSequence change. So a
    // rows rebuild (route/data change) could leave the stop stuck at #1 even
    // though the educated sequence was computed. Positioning here keeps rows
    // deterministic, so the nearest-dropdown pick and the route-dropdown educated
    // guess both persist into the save.
    // Use the sequence only when it's a valid 1-based position; until one is
    // computed (no route/stop picked yet) fall back to the end of the route
    // rather than silently front-placing the stop.
    const parsedSequence = Number(selectedSequence)
    const numericSequence = Number.isFinite(parsedSequence) && parsedSequence > 0 ? parsedSequence : formattedStops.length + 1
    const sortedRows = insertElementAtPosition(formattedStops, stopToBeAssigned, numericSequence)
    setRows(sortedRows.map((row, index) => ({ ...row, position: index })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsByRouteId, selectedRoute, stopToBeAssigned, selectedSequence])

  // Clamp the sequence to valid bounds. Clamp against the LARGER of the built
  // rows and the selected route's stop count: during load `rows` is briefly just
  // the to-be-assigned stop (length 1), and clamping a freshly-computed educated
  // sequence (e.g. 5) against that transient length reset it to 1.
  useEffect(() => {
    if (!selectedSequence) return
    const numericSequence = Number(selectedSequence)
    if (Number.isNaN(numericSequence)) return
    const maxLen = Math.max(rows.length, filteredStops.length)
    if (maxLen === 0) return
    if (numericSequence > maxLen) {
      setValue('sequence', maxLen)
    } else if (numericSequence < 1) {
      setValue('sequence', 1)
    }
  }, [selectedSequence, rows.length, filteredStops.length, setValue])

  // Refs so the debounced callback reads fresh values without recreating the
  // debounce on every render (which would cancel pending calls).
  const filteredStopsRef = useRef(filteredStops)
  const stopToBeAssignedRef = useRef(stopToBeAssigned)
  const selectedSequenceRef = useRef(selectedSequence)
  const workOrderIdRef = useRef(workOrderId)
  const routeIdRef = useRef(routeId)
  const selectedRouteRef = useRef(selectedRoute)
  filteredStopsRef.current = filteredStops
  stopToBeAssignedRef.current = stopToBeAssigned
  selectedSequenceRef.current = selectedSequence
  workOrderIdRef.current = workOrderId
  routeIdRef.current = routeId
  selectedRouteRef.current = selectedRoute

  const onSequenceChange = useCallback(
    debounce(
      () => {
        const currentFilteredStops = filteredStopsRef.current
        const currentStopToBeAssigned = stopToBeAssignedRef.current
        const currentSelectedSequence = selectedSequenceRef.current
        const currentWorkOrderId = workOrderIdRef.current
        const currentRouteId = routeIdRef.current
        const currentSelectedRoute = selectedRouteRef.current
        const formattedStops = formatStops(currentFilteredStops, currentWorkOrderId)
        const sortedRows = insertElementAtPosition(
          formattedStops,
          currentStopToBeAssigned,
          currentSelectedSequence,
          currentRouteId === currentSelectedRoute && formattedStops.find(stop => stop.workOrderId === currentWorkOrderId)
        )
        const toBeAssignedIndex = sortedRows.findIndex(element => element.toBeInserted === true)

        if (currentSelectedSequence) {
          const updatedRows = sortedRows.map((row, i) => ({ ...row, position: i }))
          setRows(updatedRows)
          const updatedPointGeojsonData = mapStopsToFeaturePoints(sortedRows)
          setRoutePointGeojsonData(updatedPointGeojsonData)
          setToBeAssignedGeojsonData([])
        } else {
          let position = 0
          const updatedRows = sortedRows.map(row => {
            if (row.workOrderId === currentWorkOrderId) {
              return { ...row, position: null }
            }
            const newRow = { ...row, position }
            position += 1
            return newRow
          })
          setRows(updatedRows)
          const uniqueFormattedStops = getUniqueStops([...formattedStops])
          const updatedPointGeojsonData = mapStopsToFeaturePoints(uniqueFormattedStops)
          const { latitude, longitude, ...rest } = currentStopToBeAssigned
          const toBeAssignedFeature = getFeaturePointFromCoordsObj({
            latitude,
            longitude,
            properties: {
              latitude,
              longitude,
              ...rest,
              icon: 'to-be-assigned-marker',
            },
          })
          setToBeAssignedGeojsonData([toBeAssignedFeature])
          setRoutePointGeojsonData(updatedPointGeojsonData)
        }

        if (toBeAssignedIndex >= 0) {
          setTimeout(() => {
            try {
              apiRef.current?.scrollToIndexes({ rowIndex: toBeAssignedIndex })
              // No programmatic refocus here: it fires onFocus -> select(), which
              // clobbers an in-progress multi-digit entry (225 -> 2). The row stays
              // in view through the scroll, so it keeps focus on its own (CS-2922).
            } catch (error) {
              // apiRef may not be ready yet — scroll is best-effort
            }
          }, 50)
        }
      },

      750
    ),
    // Empty deps — debounce never recreated, refs always read freshest values
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (!isOpen) return
    if (!apiRef.current) return
    onSequenceChange()
  }, [selectedSequence])

  useEffect(() => {
    document.documentElement.style.overflow = isOpen ? 'hidden' : 'visible'
    if (isOpen) {
      setValue('startDate', startOfWeek(serviceDate, { weekStartsOn: 0 }))
      setValue('endDate', endOfWeek(serviceDate, { weekStartsOn: 0 }))
    } else {
      handleReset()
    }
  }, [isOpen])

  // Sync selectedDays checkboxes to the stop's actual daysOfWeek once the stop
  // data is available. On-request stops have no fixed schedule — leave all days on.
  // didInitDaysRef resets on each open so the pre-selection fires once per dialog
  // session and is not re-triggered by intermediate enrichment re-renders.
  const didInitDaysRef = useRef(false)
  const stopDaysOfWeek = get(stopToBeAssigned, 'daysOfWeek', '')
  useEffect(() => {
    if (!isOpen) {
      didInitDaysRef.current = false
      return
    }
    if (isStopOnRequest || !stopDaysOfWeek || didInitDaysRef.current) return
    didInitDaysRef.current = true
    const DAY_ABBR_TO_NAME = { U: 'Sunday', M: 'Monday', T: 'Tuesday', W: 'Wednesday', R: 'Thursday', F: 'Friday', S: 'Saturday' }
    Object.entries(DAY_ABBR_TO_NAME).forEach(([abbr, name]) => {
      setValue(`selectedDays.${name}`, stopDaysOfWeek.includes(abbr))
    })
  }, [isOpen, isStopOnRequest, stopDaysOfWeek, setValue])

  useEffect(() => {
    return () => {
      document.documentElement.style.overflow = 'visible'
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      getRoutesMetadata({
        skipCache: true,
        serviceDate: formatDateToBEFormatDateFns(serviceDate),
      })
    }
  }, [isOpen, actionSerializedServiceDate])

  useHandleRouteManagerApiPolling(isOpen)

  // Safety-net nudge: route-select is when WebGL markers most commonly fail
  // to paint, so nudgeDeckOverlay's delayed 1-px panBy wakes up the DeckOverlay.
  useEffect(() => {
    if (!isRouteSelected) return undefined
    let cleanup = () => {}
    let attempts = 0
    const tryNudge = setInterval(() => {
      const map = mapRef.current?.getMap?.()
      attempts += 1
      if (map) {
        clearInterval(tryNudge)
        cleanup = nudgeDeckOverlay(map)
      } else if (attempts >= 40) {
        clearInterval(tryNudge)
      }
    }, 50)
    return () => {
      clearInterval(tryNudge)
      cleanup()
    }
  }, [isRouteSelected, selectedRoute])

  return (
    <FormProvider {...formMethods}>
      <MapProvider map={mapRef}>
        <HHFullScreenBaseDialog fullScreen open={isOpen} onClose={handleCancel}>
          <ScheduleStopDialogAppBar onClose={handleCancel} />
          <Box
            height="100%"
            overflow="hidden"
            sx={{
              ...(isOpen && {
                html: {
                  overflow: 'hidden',
                },
              }),
            }}
          >
            <Box display="flex" flexDirection="column" height="100%">
              <Grid container sx={{ flex: 1, minHeight: 0 }}>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 4,
                    lg: 4,
                    xl: 3,
                  }}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  <Box display="flex" flexDirection="column" height="100%" overflow="auto" pt={2} px={2}>
                    <ScheduleStopFiltersPanel
                      businessLineOptions={filterOptions.businessLineOptions}
                      expanded={filtersExpanded}
                      materialOptions={filterOptions.materialOptions}
                      measureOptions={filterOptions.measureOptions}
                      routesLegendData={routes}
                      serviceActionOptions={filterOptions.serviceActionOptions}
                      showDaysSelector={mapMarkerType === 'weekDay'}
                      showRoutesLegend={mapMarkerType === 'route'}
                      onExpandedChange={setFiltersExpanded}
                    />
                    {currentWorkOrderDetailsData && (
                      <Box py={1}>
                        <WorkOrderListItem
                          disableRedirect
                          formattedDate={formatDateToFEwithWeekday(serviceDate)}
                          workOrder={{
                            ...workOrderDetailsData,
                            workOrderNumber: get(workOrderDetailsData, 'workOrderSequence'),
                            serviceActionName: get(workOrderDetailsData, 'serviceAction'),
                          }}
                        />
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    {!isStopOnRequest && (
                      <Box>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          color="primary"
                          size="small"
                          sx={{
                            '& .MuiToggleButton-root': {
                              textTransform: 'none',
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'common.white',
                                '&:hover': { backgroundColor: 'primary.dark' },
                              },
                            },
                          }}
                          value={routingMode}
                          onChange={handleRoutingModeChange}
                        >
                          <ToggleButton value="nearest">{T.NEAREST_STOP_TITLE}</ToggleButton>
                          <ToggleButton value="byRoute">{T.BY_ROUTE_TITLE}</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    )}
                    {routingMode === 'nearest' && !isStopOnRequest && (
                      <Stack alignItems="center" direction="row" mt={1} spacing={0.5}>
                        <Box flex={1}>
                          <NearestStopsDropdown
                            clearable
                            isLoading={isNearestFetching}
                            MenuProps={{ sx: { zIndex: theme.zIndex.tooltip } }}
                            stops={nearestStopsList}
                            onClear={handleClearSelection}
                            onSelect={handleNearestStopSelect}
                          />
                        </Box>
                        <Tooltip arrow title={T.NEAREST_STOP_TOOLTIP}>
                          <IconButton size="small">
                            <SvgIcon fontSize="small">
                              <InformationCircle />
                            </SvgIcon>
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    {routingMode === 'byRoute' && (
                      <Box mt={1}>
                        <DesktopRoutePicker
                          clearable
                          control={control}
                          menuSx={{ zIndex: theme.zIndex.tooltip }}
                          name="selectedRoute"
                          routes={routes}
                          onChange={handleRouteChange}
                          onClear={handleClearSelection}
                        />
                      </Box>
                    )}
                    <Box flex={1} minHeight={0} position="relative" pt={2}>
                      {!isRouteSelected && !loading && (
                        <HHAlert borderColor={theme.palette.info.dark} severity="info" sx={{ mt: 1 }}>
                          {routingMode === 'nearest' ? T.SELECT_NEAREST_STOP_TO_ASSIGN : T.SELECT_ROUTE_TO_VIEW_STOPS}
                        </HHAlert>
                      )}
                      {isRouteSelected && <StopsDataGrid apiRef={apiRef} loading={loading} rows={rows} />}
                    </Box>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 8,
                    lg: 8,
                    xl: 9,
                  }}
                  sx={{ position: 'relative', height: '100%' }}
                >
                  {(loading || isNearestFetching || isMetadataFetching) && (
                    <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3 }} />
                  )}
                  {isRouteSelected ? (
                    <ScheduleStopMap
                      fitBounds={fitBounds}
                      loading={loading}
                      mapRef={mapRef}
                      routeGeojsonData={routeGeojsonData}
                      routes={routes}
                      rows={rows}
                      selectedRoute={selectedRoute}
                      serviceDate={serviceDate}
                      toBeAssignedGeojsonData={toBeAssignedGeojsonData}
                      onFitBoundsDone={() => setFitBounds(false)}
                      onStopClick={handleMapStopClick}
                    />
                  ) : (
                    <APIProvider apiKey={Constants.GOOGLE_MAPS_API_KEY}>
                      <ServiceProximityMap
                        filtersExpanded={filtersExpanded}
                        latitude={proximityMapLatitude}
                        longitude={proximityMapLongitude}
                        mapMarkerType={mapMarkerType}
                        onMarkerTypeChange={handleMarkerTypeChange}
                      />
                    </APIProvider>
                  )}
                </Grid>
              </Grid>
              <ScheduleStopDialogActions
                hidePublishMaster={isOnRequestStop(stopToBeAssigned) || !get(stopToBeAssigned, 'configuredServiceEventId')}
                isSaveDisabled={!isRouteSelected || !selectedSequence}
                loading={isSaving || isPublishing}
                masterLoading={isSavingMaster}
                publishLoading={isPublishing}
                onCancel={handleCancel}
                onPublishMasterSequence={handleSubmit(handlePublishMasterSequence)}
                onReset={routeId ? handleResetWithExistingRoute : handleReset}
                onSave={handleSubmit(handleSave)}
                onSaveMaster={handleSubmit(handleEditMasterSequence)}
              />
            </Box>
          </Box>
        </HHFullScreenBaseDialog>
      </MapProvider>
    </FormProvider>
  )
}

export default ScheduleStopDialog
